import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  inject,
  AfterViewInit, Input, signal, WritableSignal,
  NgZone,
} from '@angular/core';
import {CommonModule, DatePipe} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {Subject, takeUntil, debounceTime, Observable, merge, finalize, of} from 'rxjs';
import {catchError} from 'rxjs/operators';

// PrimeNG imports
import {ButtonModule} from 'primeng/button';
import {DialogModule} from 'primeng/dialog';
import {SelectModule} from 'primeng/select';
import {MultiSelectModule} from 'primeng/multiselect';
import {DatePickerModule} from 'primeng/datepicker';
import {CheckboxModule} from 'primeng/checkbox';
import {TooltipModule} from 'primeng/tooltip';
import {MessageService} from 'primeng/api';
import {ToastModule} from 'primeng/toast';
import {DialogService} from 'primeng/dynamicdialog';
import {DynamicDialogModule} from 'primeng/dynamicdialog';

// Components
import {OpenStreetMapComponent} from '@components/openstreetmap/openstreetmap.component';
import {MapControllerInput, MapInitOptions} from '@components/openstreetmap/interfaces';
import {ListPageWrapper} from '@app/wrappers/pages/list-page-wrapper/list-page-wrapper';
import {ListTableFiltersWrapperParent} from '@app/wrappers/pages/list-table-filters-wrapper/list-table-filters-wrapper-parent';
import {Filters} from '@components/views/filters/filters';

// Services
import {FieldManager} from './managers/field-manager.service';

// Environment
import {FirmService, Region, VarietyService} from '@6grain/masofa-monolith-api-client/dictionaries';
import {FieldList} from '@app/pages/electronic-field-map/pages/fields/field-llst/field-list';
import {DateFormatLangPipe} from '@app/utils/pipes/date-format-lang-pipe';
import {RegionManagerService} from '@components/openstreetmap/managers/region-manager.service';
import {SeasonManager} from '@app/pages/electronic-field-map/pages/gis-fields/managers/season-manager.service';
import {
  CropIdentificationPanelLegendItem,
  SEASON_LEGEND_PLAN_FACT_SWATCH_FACT,
  SEASON_LEGEND_PLAN_FACT_SWATCH_PLAN,
  SEASON_PLAN_FACT_MISMATCH_COLOR,
} from '@app/pages/electronic-field-map/pages/gis-fields/managers/season-map-legend.constants';
import {GisCircleMarker} from '@app/pages/electronic-field-map/pages/gis-fields/interfaces/gis-component.interfaces';
import {CropCacheService} from '@services/caches/crop-cache.service';
import {buildFilterQuery, Filter} from '@app/utils/_msf';
import {
  FieldDetailsDialogComponent
} from '@app/pages/electronic-field-map/pages/gis-fields/components/modals/field-details-dialog/field-details-dialog.component';
import {
  SeasonDetailsDialogComponent
} from '@app/pages/electronic-field-map/pages/gis-fields/components/modals/season-details-dialog/season-details-dialog.component';
import {FilterOperator} from '@app/utils/filter-operator';
import {
  ImportFieldModalComponent
} from '@app/pages/electronic-field-map/pages/import-fields/modals/import-field-modal/import-field-modal';
import {ExportModalComponent} from '@components/export-modal/export-modal.component';
import {Field, FieldService, SeasonService} from '@6grain/masofa-monolith-api-client/crop-monitoring';
import {
  PointPolygonDetailsModalComponent
} from '@app/pages/weather/pages/gis-weather/components/point-polygon-details-modal/point-polygon-details-modal.component';
import {environment} from '@root/src/environments/environment';
import {BackButton} from '@components/ui/back-button/back-button';

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

@Component({
  selector: 'app-gis-fields',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ButtonModule,
    DialogModule,
    DynamicDialogModule,
    SelectModule,
    MultiSelectModule,
    DatePickerModule,
    CheckboxModule,
    TooltipModule,
    ToastModule,
    OpenStreetMapComponent,
    ListPageWrapper,
    Filters,
    FieldDetailsDialogComponent,
    SeasonDetailsDialogComponent,
    ImportFieldModalComponent,
    ExportModalComponent,
    PointPolygonDetailsModalComponent,
    BackButton,
  ],
  providers: [
    MessageService,
    DatePipe,
    DateFormatLangPipe,
    DialogService
  ],
  templateUrl: './gis-fields.html',
  styleUrls: ['./gis-fields.scss']
})
export class GisFieldsComponent extends FieldList implements OnInit, AfterViewInit, OnDestroy {

  /** Below this zoom the map shows region cluster circles; at/above it loads bbox polygons/markers. */
  private static readonly DETAIL_ZOOM_THRESHOLD = 12;

  @ViewChild(OpenStreetMapComponent) openstreetmapComponent!: OpenStreetMapComponent;
  @ViewChild(FieldDetailsDialogComponent) fieldDetailsDialog!: FieldDetailsDialogComponent;
  @ViewChild(SeasonDetailsDialogComponent) seasonDetailsDialog!: SeasonDetailsDialogComponent;

  public regionManagementManager = inject(RegionManagerService);
  public fieldManager = inject(FieldManager);
  public seasonManager = inject(SeasonManager);
  public cropCacheService = inject(CropCacheService);
  private readonly ngZone = inject(NgZone);
  private destroy$ = new Subject<void>();

  /** Emits before each bbox/cluster reload so the previous HTTP is unsubscribed (= cancelled). */
  private cancelViewportRequest$ = new Subject<void>();

  /** Merge for takeUntil — one subscription cancels bbox + cluster in-flight loads. */
  private get cancelViewportLoads$(): Observable<unknown> {
    return merge(this.cancelViewportRequest$, this.destroy$);
  }
  public showImportModal: boolean = false;
  public clickedLatLong: number[] = [];
  public showWeatherModal = false;
  public clickedObj!: Field | Region | any;

  // Track previous filter type to detect changes
  private previousFilterType: string = 'field';

  public showModalExport = false;
  public exportService: WritableSignal<FieldService | SeasonService | null> = signal(null);
  public exportMethod = signal('');
  public exportCsvMethod = signal('');

  public override readonly firmService = inject(FirmService);
  public override readonly varietyService = inject(VarietyService);

  mapController$ = new Subject<MapControllerInput>();

  mapOptions: MapInitOptions = {
    height: '100%',
    center: [environment.map.lat, environment.map.lng],
    zoom: 7,
    showControls: true,
    minimalControls: false,
    drawingMode: false,
    showDrawingControls: true,
    baseMap: 'osm',
    showRegionNames: false,
    disableBounds: false
  };

  // =========================================================================
  // UI STATE
  // =========================================================================
  selectedWeatherLayer: string | null = null;
  showContextMenu: boolean = false;
  contextMenuPosition = {top: 0, left: 0};
  contextMenuData: any = null;


  mapZoom: number = 6;

  /** Last zoom from mapZoomChange — only used to detect crossing below/above {@link GisFieldsComponent.DETAIL_ZOOM_THRESHOLD}. */
  private previousZoomThresholdRef: number | null = null;

  /** Map viewport subscriptions require Leaflet to be ready; bound in {@link onMapReady}. */
  private mapViewportEventsBound = false;

  currentDrawingState = {
    isDrawing: false,
    drawingType: null as 'point' | 'polygon' | 'field' | 'rectangle' | null
  };


  filterMetaData: any = {};
  selectedDataSource: string | null = null;
  chartData: any = {labels: [], datasets: []};
  // Dynamic style for crop identification panel position
  cropPanelStyle: any = {top: '120px', right: '380px'};
  showCropIdentificationPanel: boolean = false;
  override filterModel: any = {
    type: 'field'
  };

  /** One-shot listener to close the map context menu; stable reference avoids repeated `bind`. */
  private readonly onDocumentClickCloseContextMenu = (event: Event) => {
    const contextMenuEl = document.querySelector('.map-context-menu');
    if (!contextMenuEl?.contains(event.target as Node)) {
      this.showContextMenu = false;
    }
  };

  constructor(public override translate: TranslateService) {
    super();
  }

  @Input() panelOptions: { crops?: boolean } = {
    crops: true,
  };

  /** Map page does not use field-list plan/fact multi-select (`seasonScope`). */
  protected override ensurePlanFactSelectionDefault(): void {}

  override ngOnInit(): void {

    this.exportService.set(this.fieldManager.fieldService);
    this.exportMethod.set('cropMonitoringFieldExportFieldsPost');
    this.exportCsvMethod.set('cropMonitoringFieldExportFromCSVPost');

    this.initializeComponent();
    // Skip FieldList.ngOnInit (CustomGetTotalCountByQuery); map uses refreshMapTotalCount().
    ListTableFiltersWrapperParent.prototype.ngOnInit.call(this);
    this.refreshMapTotalCount();
  }

  /** Total fields/seasons matching current map filters. */
  private refreshMapTotalCount(): void {
    const filters = (this.filtersParams ?? []).filter(
      (f: {filterField?: string}) => f.filterField !== 'type',
    );

    const request$ =
      this.filterModel?.type === 'season'
        ? this.seasonManager.seasonService.cropMonitoringSeasonGetTotalCountPost({
            filters: filters as any,
          })
        : this.fieldManager.fieldService.cropMonitoringFieldGetTotalCountPost({
            filters: filters as any,
          });

    request$
      .pipe(takeUntil(this.destroy$), catchError(() => of(0)))
      .subscribe((total) => this.totalRecords.set(Number(total) || 0));
  }

  private async refreshMapTotalCountWithRegionScope(): Promise<void> {
    await this.regionManagement.includeChildInRequest(
      this.filterModel as Record<string, unknown>,
      this.filtersParams as Filter[],
    );
    this.refreshMapTotalCount();
  }

  override initFiltersForm(): void {
    this.filtersForms = [
      {
        name: 'type',
        title: 'Type',
        type: 'select',
        selectOptions: [
          {name: 'Culture', id: 'season'},
          {name: 'Field', id: 'field'},
        ],
        options: {
          clearable: false,
          select: {optionLabel: 'name', translateBy: 'locale',}
        }
      },
      {
        name: 'cropType',
        title: 'PLAN_FACT',
        type: 'select',
        selectOptions: [
          {name: 'PLAN_FACT_PLAN', id: 'plan'},
          {name: 'PLAN_FACT_FACT', id: 'fact'},
          {name: 'PLAN_FACT_PLAN_AND_FACT', id: 'both'},
        ],
        options: {
          clearable: false,
          select: {optionLabel: 'name', translateBy: 'locale',}
        },
        hide: () => this.filterModel['type'] !== 'season',
      },
      {
        name: 'planMatch',
        title: 'PLAN_MATCH_FILTER',
        type: 'radio-box',
        options: {
          radioBox: {
            allLabel: 'ALL',
            yesLabel: 'PLAN_MATCH_FILTER_TRUE',
            noLabel: 'PLAN_MATCH_FILTER_FALSE',
            direction: 'horizontal'
          }
        },
        hide: () =>
          this.filterModel['type'] !== 'season' ||
          (this.filterModel['cropType'] ?? 'fact') !== 'both',
      },
      {
        name: 'plantingDate',
        title: 'PLANTING_DATE_FROM',
        placeholder: 'dd.mm.yyyy',
        type: 'date',
        options: {date: {selectionMode: 'single', maxDateFromField: 'harvestingDate'}},

        hide: () => {
          if (this.filterModel['type'] !== 'season') {
            return true;
          }
          const m = this.filterModel['cropType'] ?? 'fact';
          return m === 'plan';
        },
      },
      {
        name: 'harvestingDate',
        title: 'HARVESTING_DATE_TO',
        placeholder: 'dd.mm.yyyy',
        type: 'date',
        options: {date: {selectionMode: 'single'}},
        hide: () => {
          if (this.filterModel['type'] !== 'season') {
            return true;
          }
          const m = this.filterModel['cropType'] ?? 'fact';
          return m === 'plan';
        }
      },
      {
        name: 'plantingDatePlan',
        title: 'PLANNED_SOWING_PERIOD',
        placeholder: 'dd.mm.yyyy',
        type: 'date',
        options: {date: {selectionMode: 'range'}},
        hide: () => {
          if (this.filterModel['type'] !== 'season') {
            return true;
          }
          const m = this.filterModel['cropType'] ?? 'fact';
          return m === 'fact';
        },
      },
      {
        name: 'harvestingDatePlan',
        title: 'PLANNED_HARVESTING_PERIOD',
        placeholder: 'dd.mm.yyyy',
        type: 'date',
        options: {date: {selectionMode: 'range'}},
        hide: () => {
          if (this.filterModel['type'] !== 'season') {
            return true;
          }
          const m = this.filterModel['cropType'] ?? 'fact';
          return m === 'fact';
        },
      },
      {
        name: 'id',
        title: 'Field name',
        type: 'select-paginate',
        selectService: this.service,
        selectServiceMethod: 'cropMonitoringFieldGetByQueryPost',
        placeholder: 'Enter field name',
        options: {
          select: {
            optionLabel: 'name',
            optionValue: 'id',
            searchBy: 'name',
            translateBy: 'none'
          }
        },
        hide: () => {
          return this.filterModel['type'] && this.filterModel['type'] !== 'field';
        }
      },
      {
        name: 'fieldId',
        title: 'Field name',
        type: 'select-paginate',
        selectService: this.service,
        selectServiceMethod: 'cropMonitoringFieldGetByQueryPost',
        placeholder: 'Enter field name',
        options: {
          select: {
            optionLabel: 'name',
            optionValue: 'id',
            searchBy: 'name',
            translateBy: 'none'
          }
        },
        hide: () => this.filterModel['type'] !== 'season',
      },
      {
        title: 'Region',
        name: 'regionId',
        type: 'tree-select',
        selectService: this.regionManagement,
        selectServiceMethod: 'getRegionsObservable',
        options: {
          treeSelect: {
            treeBy: 'parentId',
          }
        },
        placeholder: 'Select region',
      },
      {
        name: 'fieldArea',
        title: 'Field area',
        type: 'input-double',
        placeholder: 'Enter field area',
        hide: () => {
          return this.filterModel['type'] && this.filterModel['type'] !== 'field';
        }
      },
      {
        name: 'CropId',
        title: 'Culture',
        type: 'select',
        selectService: this.cropService,
        selectServiceMethod: 'dictionariesCropGetByQueryPost',
        hide: () => {
          return this.filterModel['type'] && this.filterModel['type'] !== 'season';
        }
      },
      {
        name: 'plannedCropId',
        title: 'PLANNED_CROP_ID',
        type: 'select',
        selectService: this.cropService,
        selectServiceMethod: 'dictionariesCropGetByQueryPost',
        hide: () => {
          return this.filterModel['type'] && this.filterModel['type'] !== 'season';
        }
      },
      {
        type: 'select-paginate',
        name: 'agricultureProducerId',
        title: 'Agricultural chemical producer',
        placeholder: 'Agricultural chemical producer',
        selectService: this.firmService,
        options: {
          select: {
            optionLabel: 'shortName',
            searchBy: 'InnOrName',
            translateBy: 'none',
            optionSubtitle:'inn',
            filter:[
              {
                filterField: "businessTypeId",
                filterOperator: 0,
                filterValue: "019c484e-634c-7191-b424-9ed085e59147"
              }
            ]
          }
        },
        hide: () => {
          return this.filterModel['type'] && this.filterModel['type'] !== 'field';
        },
        selectServiceMethod: 'dictionariesFirmGetByInnOrNamePost',
      },
      {
        name: 'cadastre',
        title: 'Cadastre',
        type: 'select',
        placeholder: 'Select cadastre number',
        selectService: this.cadastreService,
        options: {select: {optionLabel: 'cadastralNumber', translateBy: 'none'}},
        selectServiceMethod: 'dictionariesCadastreGetByQueryPost',
        hide: () => {
          return this.filterModel['type'] && this.filterModel['type'] !== 'field';
        }
      },
      {
        name: 'monitoring',
        title: 'IS_MONITORING',
        type: 'checkbox',
        hide: () => this.filterModel['type'] !== 'season',
      },
      {
        name: 'surveyBlockType',
        title: 'SURVEY_BLOCK_TYPE',
        type: 'select',
        placeholder: 'SURVEY_BLOCK_TYPE',
        selectOptions: [
          {name: 'MANUAL_SURVEY_BLOCKERS', id: 'manual'},
          {name: 'REMOTE_SURVEY_BLOCKERS', id: 'remote'},
        ],
        options: {
          clearable: true,
          select: {optionLabel: 'name', translateBy: 'locale'},
        },
        hide: () => this.filterModel['type'] !== 'season',
      },
    ];
  }


  override ngAfterViewInit(): void {
    super.ngAfterViewInit();
    setTimeout(() => {
      this.loadRegions();
    }, 500);
  }

  ngOnDestroy(): void {
    this.cancelViewportRequest$.next();
    this.destroy$.next();
    this.destroy$.complete();
    this.cancelViewportRequest$.complete();
    this.mapViewportEventsBound = false;
  }

  // =========================================================================
  // INITIALIZATION
  // =========================================================================

  /** Leaflet ready: map zoom / drag observables exist (subscribe here, not in ngAfterViewInit). */
  onMapReady(): void {
    this.syncZoomFromMapInstance();
    this.subscribeToMapEvents();
  }

  private syncZoomFromMapInstance(): void {
    try {
      const z = this.openstreetmapComponent?.getMapInstance?.()?.getZoom?.();
      if (typeof z === 'number' && !Number.isNaN(z)) {
        this.mapZoom = z;
      }
    } catch {
      /* ignore */
    }
  }

  /**
   * Initialize component
   */
  private initializeComponent(): void {
    this.mapOptions.center = [environment.map.lat, environment.map.lng];
    this.mapOptions.zoom = 6;
  }

  /**
   * When switching field ↔ season, sync export API targets, strip type-specific `filterModel` keys, and reset applied filters.
   */
  private applyFilterTypeSwitchIfNeeded(): void {
    if (this.filterModel.type === this.previousFilterType) {
      return;
    }
    this.previousFilterType = this.filterModel.type;

    if (this.filterModel.type === 'field') {
      this.exportService.set(this.fieldManager.fieldService);
      this.exportMethod.set('cropMonitoringFieldExportFieldsPost');
      this.exportCsvMethod.set('cropMonitoringFieldExportFromCSVPost');
      delete this.filterModel.plantingDate;
      delete this.filterModel.harvestingDate;
      delete this.filterModel.plantingDatePlan;
      delete this.filterModel.harvestingDatePlan;
      delete this.filterModel.plantingDatePlanFrom;
      delete this.filterModel.plantingDatePlanTo;
      delete this.filterModel.harvestingDatePlanFrom;
      delete this.filterModel.harvestingDatePlanTo;
      delete this.filterModel.cropId;
      delete this.filterModel.monitoring;
      delete this.filterModel.planMatch;
      delete this.filterModel.cropType;
      delete this.filterModel.PlantingDate;
      delete this.filterModel.HarvestingDate;
      delete this.filterModel.CropId;
      delete this.filterModel.plannedCropId;
      delete this.filterModel.fieldId;
      delete this.filterModel.surveyBlockType;
    } else if (this.filterModel.type === 'season') {
      this.exportService.set(this.seasonManager.seasonService);
      this.exportMethod.set('cropMonitoringSeasonExportSeasonsPost');
      this.exportCsvMethod.set('cropMonitoringSeasonExportFromCSVPost');
      delete this.filterModel.id;
      delete this.filterModel.fieldArea;
      delete this.filterModel.agricultureProducerId;
      delete this.filterModel.plantingDate;
      delete this.filterModel.harvestingDate;
      this.filterModel.cropType = 'fact';
    }
    this.filtersParams = [];
  }

  private setOsmLoading(loading: boolean): void {
    if (this.openstreetmapComponent) {
      this.openstreetmapComponent.isLoading = loading;
    }
  }

  private isMapInDrawingMode(): boolean {
    const osm = this.openstreetmapComponent;
    return !!(
      osm?.isDrawingRectangle() ||
      osm?.isDrawingPolygon() ||
      osm?.isDrawingBoxPolygon() ||
      osm?.isDrawingCustomPolygon()
    );
  }

  private enrichSeasonDataForDialog(data: Record<string, unknown>): void {
    if (data['cropId']) {
      data['cropName'] = this.cropCacheService.getCropNameById(String(data['cropId']), true);
    }
    const plannedId = data['plannedCropId'] as string | undefined;
    if (plannedId) {
      data['plannedCropName'] = this.cropCacheService.getCropNameById(String(plannedId), true);
    }
  }

  /** Updates region/district header state after loading a field or season with a `regionId`. */
  private updateRegionBreadcrumbFromId(regionId: unknown): void {
    if (regionId == null) {
      return;
    }
    this.filterModel.regionId = regionId;
    this.regionManagementManager
      .getRegions(regionId as any)
      .then(regs => {
        if (!regs?.length) {
          return;
        }
        const region = regs[0];
        if (region.level === 3) {
          this.regionManagementManager.hoveredDistrict = region;
          if (region.parentId) {
            this.regionManagementManager
              .getRegions(region.parentId)
              .then(parentRegs => {
                if (parentRegs?.length) {
                  this.regionManagementManager.hoveredRegion = parentRegs[0];
                }
              })
              .catch(() => undefined);
          }
        } else if (region.level === 2) {
          this.regionManagementManager.hoveredRegion = region;
          this.regionManagementManager.hoveredDistrict = null;
        } else {
          this.regionManagementManager.hoveredRegion = region;
          this.regionManagementManager.hoveredDistrict = null;
        }
      })
      .catch(() => undefined);
  }

  /**
   * Subscribe to map events (call after `mapReady`).
   */
  private subscribeToMapEvents(): void {
    if (this.mapViewportEventsBound) {
      return;
    }

    const osm = this.openstreetmapComponent;
    if (!osm?.mapZoomChange$ || !osm?.mapDragEnd$) {
      return;
    }

    this.mapViewportEventsBound = true;

    if (osm.mapRightClick$) {
      osm.mapRightClick$
        .pipe(takeUntil(this.destroy$))
        .subscribe((event: any) => {
          this.handleMapRightClick(event);
        });
    }

    osm.mapZoomChange$
      .pipe(takeUntil(this.destroy$))
      .subscribe((zoom: number) => {
        const threshold = GisFieldsComponent.DETAIL_ZOOM_THRESHOLD;
        const zoomBeforeEvent = this.previousZoomThresholdRef;
        this.mapZoom = zoom;

        try {
          if (zoom >= threshold) {
            // First zoom after init (unknown ref) or crossing below → at/above threshold; not 12→13, 13→14…
            if (zoomBeforeEvent === null || zoomBeforeEvent < threshold) {
              this.loadPolygons();
            }
          } else {
            try {
              this.fieldManager.clearFieldLayers();
              this.seasonManager.clearSeasons();
            } catch {
              /* ignore */
            }
            this.loadCluster();
          }
        } catch {
          /* ignore */
        }
        this.previousZoomThresholdRef = zoom;
      });

    try {
      if (osm.polygonClick$) {
        osm.polygonClick$
          .pipe(takeUntil(this.destroy$))
          .subscribe((evt: any) => {
            if (this.isMapInDrawingMode()) {
              return;
            }
            this.handlePolygonClick(evt);
          });
      }

      if (osm.markerClick$) {
        osm.markerClick$
          .pipe(takeUntil(this.destroy$))
          .subscribe((evt: any) => {
            this.handleMarkerClick(evt);
          });
      }

      this.fieldManager.fieldPolygonClick$
        .pipe(takeUntil(this.destroy$))
        .subscribe(evt => {
          if (this.filterModel.type !== 'field') {
            return;
          }
          const field = evt?.data?.field as Field | undefined;
          const fieldId = field?.id ?? evt?.data?.fieldId;
          if (fieldId == null || String(fieldId).trim() === '' || String(fieldId) === EMPTY_GUID) {
            return;
          }
          this.showWeatherModal = false;
          const payload = field?.id ? field : { id: String(fieldId) };
          this.ngZone.run(() => this.fieldDetailsDialog?.show(payload));
        });

      this.seasonManager.seasonPolygonClick$
        .pipe(takeUntil(this.destroy$))
        .subscribe(evt => {
          if (this.filterModel.type !== 'season') {
            return;
          }
          this.openSeasonDetailsFromVtOrPolygonEvent(evt);
        });
    } catch {
      /* ignore */
    }

    /** Pan only: debounced bbox reload after drag (zoom handled in `mapZoomChange$` above). */
    osm.mapDragEnd$
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(500)
      )
      .subscribe((event: any) => {
        const currentZoom = event?.zoom ?? this.mapZoom;
        if (currentZoom >= GisFieldsComponent.DETAIL_ZOOM_THRESHOLD) {
          this.loadPolygons();
        }
      });
  }


  private handleMapRightClick(event: any): void {
    // Show context menu at the click position
    this.showContextMenu = true;
    this.contextMenuPosition = {
      top: event.containerPoint?.y || 0,
      left: event.containerPoint?.x || 0
    };

    // Store the click data for context menu actions
    this.contextMenuData = {
      lat: event.lat,
      lng: event.lng,
      originalEvent: event.originalEvent,
      containerPoint: event.containerPoint
    };

    // Close context menu when clicking elsewhere on the map
    setTimeout(() => {
      document.addEventListener('click', this.onDocumentClickCloseContextMenu, {once: true});
    }, 100);
  }

  /**
   * Toggle region names display
   */
  toggleRegionNames(): void {
    this.mapController$.next({
      setShowRegionNames: this.mapOptions.showRegionNames
    });
  }


  private loadPolygons(): void {
    if (!this.openstreetmapComponent || !this.openstreetmapComponent.isMapInitialized) {
      return;
    }

    const mapInstance = this.openstreetmapComponent.getMapInstance();
    if (!mapInstance || typeof mapInstance.getBounds !== 'function') {
      return;
    }

    try {
      /** Cancel previous field/season bbox request (still in flight → HttpClient unsubscribes = abort). */
      this.cancelViewportRequest$.next();

      this.fieldManager.initialize(this.openstreetmapComponent);
      this.seasonManager.initialize(this.openstreetmapComponent);

      this.setOsmLoading(true);

      const bounds = mapInstance.getBounds();
      // Leaflet bounds: _southWest and _northEast
      const southWest = bounds.getSouthWest();
      const northEast = bounds.getNorthEast();


      this.filtersParams = this.filtersParams.filter((filter: any) => filter.filterField !== 'type');

      const query = {
        filters: this.filtersParams as any
      };

      const params = {
        west: southWest.lng,
        south: southWest.lat,
        east: northEast.lng,
        north: northEast.lat,
        query
      } as any;

      if (this.filterModel.type === 'field') {
        this.fieldManager.loadField(params)
          .pipe(
            takeUntil(this.cancelViewportLoads$),
            finalize(() => this.setOsmLoading(false)),
          )
          .subscribe();
      } else {
        this.seasonManager.setPlanFactDisplayMode(this.filterModel.cropType);
        this.seasonManager.loadSeason(params)
          .pipe(
            takeUntil(this.cancelViewportLoads$),
            finalize(() => this.setOsmLoading(false)),
          )
          .subscribe();
      }
    } catch {
      /* ignore */
    }
  }

  private loadCluster(): void {
    if (!this.openstreetmapComponent || !this.openstreetmapComponent.isMapInitialized) {
      return;
    }

    try {
      this.cancelViewportRequest$.next();

      this.fieldManager.initialize(this.openstreetmapComponent);
      this.seasonManager.initialize(this.openstreetmapComponent);


      this.fieldManager.clearFields();
      this.seasonManager.clearSeasons();

      this.mapController$.next({
        clearCircleMarkers: true
      });

      this.filtersParams = this.filtersParams.filter((filter: any) => filter.filterField !== 'type');

      const query = {
        filters: this.filtersParams as any
      };


      this.setOsmLoading(true);

      let request: Observable<GisCircleMarker[]>;
      if (this.filterModel.type === 'field') {
        request = this.fieldManager.getRegionsFieldCount(query);
      } else {
        request = this.seasonManager.getRegionsSeasonCount(query);
      }
      request
        .pipe(
          takeUntil(this.cancelViewportLoads$),
          finalize(() => this.setOsmLoading(false)),
        )
        .subscribe({
          next: gisCircles => {
            this.mapController$.next({
              addCircleMarkers: gisCircles
            });
          },
        });

    } catch {
      /* ignore */
    }
  }


  loadMapData(): void {
    if (this.mapZoom >= GisFieldsComponent.DETAIL_ZOOM_THRESHOLD) {
      this.loadPolygons();
    } else {
      this.loadCluster();
    }
  }

  private loadRegions(): void {

    this.setOsmLoading(true);

    this.regionManagementManager.toPolygon()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (polygons) => {
          this.setOsmLoading(false);

          if (polygons && polygons.length > 0) {

            this.regionManagementManager.configurePolygonEvents(
              polygons,
              this.mapOptions,
              (lat, long, region) => {
                if (this.fieldDetailsDialog.visible || this.seasonDetailsDialog.visible) {
                  return;
                }
                this.openWeatherModal(region, lat, long, true);
              },
            );
            this.mapController$.next({
              addGisPolygons: polygons
            });

            setTimeout(() => {
              this.mapOptions.showRegionNames = true;
              this.toggleRegionNames();
            });

            this.loadMapData();
            this.cropCacheService.getCrops()
              .pipe(takeUntil(this.destroy$))
              .subscribe();
          }
        },
        error: (err) => {
          this.setOsmLoading(false);
          console.error('Error loading regions:', err);
        }
      });
  }

  /**
   * Clear all filters
   */
  override clearFilters(): void {


    this.filtersParams = [];
    this.filterModel = {
      type: 'field'
    };
    this.toggleFilter = !this.toggleFilter;
    this.refreshMapTotalCount();
    this.resetMapFilters();
  }


  private resetMapFilters(): void {
    this.regionManagementManager.filterByRegion(undefined, this.openstreetmapComponent.getLayersController());
    this.fieldManager.clearFieldLayers();
    this.seasonManager.clearSeasons();

    this.loadCluster();
    // Reset map view to default
    const layersController = this.openstreetmapComponent?.getLayersController();
    if (layersController) {
      layersController.centerToPoint(
        environment.map.lat,
        environment.map.lng,
        6,
        true
      );
    }
  }


  // =========================================================================
  // MAP LAYER MANAGEMENT
  // =========================================================================


  filterFieldsChange(_changedModel: any): void {
    this.applyFilterTypeSwitchIfNeeded();
    if (
      this.filterModel.type === 'season' &&
      (this.filterModel.cropType ?? 'fact') !== 'both'
    ) {
      delete this.filterModel.planMatch;
    }
  }

  override async applyFilters(): Promise<void> {

    const openStreetMap = this.openstreetmapComponent;

    if (openStreetMap && !this.filterModel.regionId) {
      openStreetMap.mapInstance.setZoom(6);
    }

    this.regionManagementManager.filterByRegion(
      this.filterModel.regionId,
      this.openstreetmapComponent.getLayersController()
    );

    this.applyFilterTypeSwitchIfNeeded();

    // Normal filter change flow
    this._applyFilters();


    if (this.filterModel?.id && this.filterModel.type === 'field') {
      try {
        const query: { filters: Filter[] } = {
          filters: [
            {
              filterField: 'id',
              filterOperator: FilterOperator.Equals,
              filterValue: this.filterModel.id
            }
          ]
        };

        const params: { west: number; east: number; south: number; north: number; query: { filters: Filter[] } } = {
          west: -180,
          east: 180,
          south: -90,
          north: 90,
          query: query
        };

        try {
          this.fieldManager.initialize(this.openstreetmapComponent);
        } catch {
          // ignore
        }

        this.setOsmLoading(true);
        this.fieldManager.loadField(params, true)
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => this.setOsmLoading(false)),
          )
          .subscribe((fields: Field[]) => {
            try {
              const f = fields?.[0] as { regionId?: unknown } | undefined;
              if (f?.regionId != null) {
                this.updateRegionBreadcrumbFromId(f.regionId);
              }
            } catch {
              /* ignore */
            }
          });

        await this.refreshMapTotalCountWithRegionScope();
        return;
      } catch {
        /* ignore */
      }
    }

    if (this.filterModel?.fieldId && this.filterModel.type === 'season') {
      try {
        this.seasonManager.setPlanFactDisplayMode(this.filterModel.cropType);
        const filtersForQuery = this.filtersParams.filter(
          (f: any) => f.filterField !== 'type'
        );
        const params = {
          west: -180,
          east: 180,
          south: -90,
          north: 90,
          query: {filters: filtersForQuery as Filter[]}
        };

        try {
          this.seasonManager.initialize(this.openstreetmapComponent);
        } catch {
          // ignore
        }

        this.setOsmLoading(true);
        this.seasonManager.loadSeason(params, true)
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => this.setOsmLoading(false)),
          )
          .subscribe({
            next: (_seasons: Field[]) => {
              try {
                const s = (_seasons as unknown as any[])?.[0] as { regionId?: unknown; field?: { regionId?: unknown } } | undefined;
                const rid = s?.regionId ?? s?.field?.regionId;
                if (rid != null) {
                  this.updateRegionBreadcrumbFromId(rid);
                }
              } catch {
                /* ignore */
              }
            },
          });

        await this.refreshMapTotalCountWithRegionScope();
        return;
      } catch {
        /* ignore */
      }
    }

    await this.refreshMapTotalCountWithRegionScope();
    this.loadMapData();
  }


  /**
   * Plan date range pickers: `plantingDatePlan` / `harvestingDatePlan` `[start, end]` → two API filters
   * (`GreaterThanOrEqual` / `LessThanOrEqual`), values `yyyy-MM-dd`.
   */
  private expandPlannedPlanDateRanges(filters: Filter[]): Filter[] {
    const out: Filter[] = [];
    for (const filter of filters) {
      const field = filter.filterField ?? '';
      if (
        (field === 'plantingDatePlan' || field === 'harvestingDatePlan') &&
        Array.isArray(filter.filterValue)
      ) {
        const [startRaw, endRaw] = filter.filterValue as [
          Date | string | number | null | undefined,
          Date | string | number | null | undefined,
        ];
        if (startRaw != null && startRaw !== '') {
          const d = startRaw instanceof Date ? startRaw : new Date(startRaw as string | number);
          if (!Number.isNaN(d.getTime())) {
            const fmt = this.datePipe.transform(d, 'yyyy-MM-dd');
            if (fmt) {
              out.push({
                filterField: field,
                filterOperator: FilterOperator.GreaterThanOrEqual,
                filterValue: fmt,
              });
            }
          }
        }
        if (endRaw != null && endRaw !== '') {
          const d = endRaw instanceof Date ? endRaw : new Date(endRaw as string | number);
          if (!Number.isNaN(d.getTime())) {
            const fmt = this.datePipe.transform(d, 'yyyy-MM-dd');
            if (fmt) {
              out.push({
                filterField: field,
                filterOperator: FilterOperator.LessThanOrEqual,
                filterValue: fmt,
              });
            }
          }
        }
      } else {
        out.push(filter);
      }
    }
    return out;
  }

   _applyFilters() {
    const _filterModel: Record<string, unknown> = {...this.filterModel};

    delete _filterModel['cropType'];
    delete _filterModel['surveyBlockType'];

    if (_filterModel['type'] === 'field') {
      delete _filterModel['fieldId'];
      delete _filterModel['planMatch'];
    } else if (_filterModel['type'] === 'season') {
      delete _filterModel['id'];
      if ((this.filterModel['cropType'] ?? 'fact') !== 'both') {
        delete _filterModel['planMatch'];
      }
    }

    if (_filterModel['monitoring'] !== true) {
      delete _filterModel['monitoring'];
    }

    if (_filterModel['planMatch'] !== true && _filterModel['planMatch'] !== false) {
      delete _filterModel['planMatch'];
    }

    const mode = (this.filterModel['cropType'] ?? 'fact') as string;

    delete _filterModel['plantingDatePlanFrom'];
    delete _filterModel['plantingDatePlanTo'];
    delete _filterModel['harvestingDatePlanFrom'];
    delete _filterModel['harvestingDatePlanTo'];

    if (mode === 'plan') {
      delete _filterModel['plantingDate'];
      delete _filterModel['harvestingDate'];
    } else if (mode === 'fact') {
      delete _filterModel['plantingDatePlan'];
      delete _filterModel['harvestingDatePlan'];
    }

    this.filtersParams = buildFilterQuery(_filterModel as any);
    this.filtersParams = this.expandPlannedPlanDateRanges(this.filtersParams as Filter[]);

    this.filtersParams.forEach((filter: any) => {
      if (
        filter.filterField === 'plantingDate' &&
        filter.filterValue != null &&
        filter.filterValue !== ''
      ) {
        filter.filterOperator = FilterOperator.GreaterThanOrEqual;
        filter.filterValue = this.datePipe.transform(filter.filterValue, 'yyyy-MM-dd') as string;
      } else if (
        filter.filterField === 'harvestingDate' &&
        filter.filterValue != null &&
        filter.filterValue !== ''
      ) {
        filter.filterOperator = FilterOperator.LessThanOrEqual;
        filter.filterValue = this.datePipe.transform(filter.filterValue, 'yyyy-MM-dd') as string;
      }
    });

    if (this.filterModel['type'] === 'season') {
      const cropMode = (this.filterModel['cropType'] ?? 'fact') as string;
      const isPlannedFilter = SeasonManager.buildIsPlannedFilterForPlanFactMode(cropMode);
      if (isPlannedFilter) {
        this.filtersParams.push(isPlannedFilter);
      }

      const pushNotNull = (field: string) => {
        this.filtersParams.push({
          filterField: field,
          filterOperator: FilterOperator.NotEquals,
          filterValue: null,
        });
      };

      const surveyBlockType = this.filterModel['surveyBlockType'];
      if (surveyBlockType === 'manual') {
        pushNotNull('manualSurveyBlockReasonId');
      } else if (surveyBlockType === 'remote') {
        pushNotNull('remoteSurveyBlockReasonId');
      }
    }

    this.toggleFilter = !this.toggleFilter;

  }


  /**
   * Get current display mode
   */
  getRegionPathString(): string {
    return this.regionManagementManager.getRegionPathString(
      this.translate.instant(environment.country),
      this.translate.instant('ALL_REGIONS')
    );
  }


  getCropIdentificationList(): CropIdentificationPanelLegendItem[] {
    const crops: CropIdentificationPanelLegendItem[] = [
      {nameKey: 'COTTON', kind: 'crop', color: '#c0c0c0'},
      {nameKey: 'WHEAT', kind: 'crop', color: '#ffe119'},
      {nameKey: 'CORN', kind: 'crop', color: '#00FFFF'},
      {nameKey: 'RICE', kind: 'crop', color: '#ffffff'},
      {nameKey: 'SOYBEAN', kind: 'crop', color: '#78A8FC'},
      {nameKey: 'SUNFLOWER', kind: 'crop', color: '#ffbf00'},
      {nameKey: 'TOMATO', kind: 'crop', color: '#e6194b'},
      {nameKey: 'POTATO', kind: 'crop', color: '#D0AB8B'},
      {nameKey: 'MUNG_BEAN', kind: 'crop', color: '#AAFFC3'},
      {nameKey: 'WATERMELON', kind: 'crop', color: '#f032e6'},
      {nameKey: 'ONION', kind: 'crop', color: '#ffcc99'},
      {nameKey: 'CARROT', kind: 'crop', color: '#ff7f00'},
      {nameKey: 'CUCUMBER', kind: 'crop', color: '#008000'},
      {nameKey: 'HOT_PEPPER', kind: 'crop', color: '#800000'},
      {nameKey: 'CHICKPEA', kind: 'crop', color: '#e6beff'},
      {nameKey: 'CABBAGE', kind: 'crop', color: '#a2c523'},
      {nameKey: 'ORCHARD_WALNUT', kind: 'crop', color: '#D8B99D'},
      {nameKey: 'ORCHARD_APPLE', kind: 'crop', color: '#bcf60c'},
      {nameKey: 'ORCHARD_VINEYARD', kind: 'crop', color: '#6750A4'},
      {nameKey: 'ORCHARD_POMEGRANATE', kind: 'crop', color: '#990033'},
    ];

    const mapStatusLegends: CropIdentificationPanelLegendItem[] = [
      {nameKey: 'GIS_FIELDS_LEGEND_SURVEY_BLOCKED', kind: 'surveyBlocked'},
      {
        nameKey: 'GIS_FIELDS_LEGEND_PLAN_FACT_CORRESPONDENCE',
        kind: 'planFactCorrespondence',
        color: SEASON_LEGEND_PLAN_FACT_SWATCH_FACT,
      },
      {
        nameKey: 'GIS_FIELDS_LEGEND_PLAN_FACT_MISMATCH',
        kind: 'planFactMismatch',
        color: SEASON_PLAN_FACT_MISMATCH_COLOR,
      },
    ];

    // Sort crops alphabetically based on translated name
    const currentLang = this.translate.currentLang || 'ru';
    let locale = 'ru-RU';

    if (currentLang.includes('en')) {
      locale = 'en-US';
    } else if (currentLang.includes('uz')) {
      locale = 'uz-Latn-UZ';
    }

    const sortedCrops = crops.sort((a, b) => {
      const nameA = this.translate.instant(a.nameKey);
      const nameB = this.translate.instant(b.nameKey);

      return nameA.localeCompare(nameB, locale, {
        sensitivity: 'base',
        numeric: true,
        ignorePunctuation: true
      });
    });

    return [...sortedCrops, ...mapStatusLegends];
  }

  /** CSS variables for plan/fact stripe legend swatch (matches map hatch demo colors). */
  get planFactLegendSwatchVars(): Record<string, string> {
    return {
      '--fdd-legend-plan-fact-fact': SEASON_LEGEND_PLAN_FACT_SWATCH_FACT,
      '--fdd-legend-plan-fact-plan': SEASON_LEGEND_PLAN_FACT_SWATCH_PLAN,
    };
  }

  // =========================================================================
  // FIELD DETAILS MODAL METHODS
  // =========================================================================
  /** Season details from VT/polygon click; closes weather panel first. */
  private openSeasonDetailsFromVtOrPolygonEvent(evt: any): void {
    const raw = evt?.data?.season ?? evt?.data;
    if (!raw || (raw.id == null && raw.seasonId == null)) {
      return;
    }
    const data: Record<string, unknown> = {...raw};
    this.enrichSeasonDataForDialog(data);
    this.showWeatherModal = false;
    this.seasonDetailsDialog.show(data);
  }

  private handlePolygonClick(event: any): void {
    try {
      const rawData =
        event?.data ??
        event?.properties?.data ??
        event?.feature?.properties?.data ??
        null;
      const elementId = event?.id != null ? String(event.id) : '';

      if (elementId.includes('season_')) {
        if (!rawData) {
          return;
        }
        this.enrichSeasonDataForDialog(rawData as Record<string, unknown>);
        this.showWeatherModal = false;
        this.ngZone.run(() => this.seasonDetailsDialog?.show(rawData));
        return;
      }

      const fieldObjFull = rawData ? ((rawData as any).field ?? rawData) : null;
      let fieldId: string | null =
        fieldObjFull?.id != null ? String(fieldObjFull.id).trim() : this.extractFieldIdFromPolygonMapEvent(event);
      if (!fieldId || fieldId === EMPTY_GUID) {
        fieldId = this.parseFieldIdFromPolygonElementId(elementId);
      }
      if (!fieldId || fieldId === EMPTY_GUID) {
        return;
      }

      this.showWeatherModal = false;
      const payload = fieldObjFull?.id ? fieldObjFull : { id: fieldId };
      this.ngZone.run(() => this.fieldDetailsDialog?.show(payload));
    } catch {
      /* ignore */
    }
  }

  /** Identifiant parcelle depuis propriétés du clic polygone / tuiles (hors `data` complet). */
  private extractFieldIdFromPolygonMapEvent(event: any): string | null {
    const d = event?.data;
    const candidates = [
      d?.fieldId,
      d?.field?.id,
      d?.id,
      event?.properties?.fieldId,
      event?.properties?.data?.fieldId,
      event?.properties?.data?.field?.id,
      event?.properties?.data?.id,
      event?.feature?.properties?.fieldId,
      event?.feature?.properties?.data?.field?.id,
    ];
    for (const c of candidates) {
      if (c != null && String(c).trim() !== '' && String(c) !== EMPTY_GUID) {
        return String(c).trim();
      }
    }
    return null;
  }

  private parseFieldIdFromPolygonElementId(elementId: string): string | null {
    if (!elementId || elementId.includes('season_')) {
      return null;
    }
    if (elementId.startsWith('field_marker_')) {
      const rest = elementId.slice('field_marker_'.length);
      return rest || null;
    }
    if (elementId.startsWith('field_')) {
      const rest = elementId.slice('field_'.length);
      return rest || null;
    }
    return null;
  }

  private handleMarkerClick(event: any): void {
    try {
      // markers send data in event?.data
      const data = event?.data || event?.properties?.data || null;
      if (!data) return;

      if (data.seasonId && !data.field) {
        this.enrichSeasonDataForDialog(data as Record<string, unknown>);
        this.showWeatherModal = false;
        this.ngZone.run(() => this.seasonDetailsDialog?.show(data));
        return;
      }

      // If the marker contains a field object, show it
      if (data.field) {
        this.showWeatherModal = false;
        this.ngZone.run(() => this.fieldDetailsDialog?.show(data.field));
        return;
      }

      // If it contains fieldId, try to fetch
      const fieldId = data.fieldId || data.field?.id || data.id;
      if (fieldId && String(fieldId) !== EMPTY_GUID) {
        this.showWeatherModal = false;
        this.ngZone.run(() =>
          this.fieldDetailsDialog?.show(data.field?.id ? data.field : { id: String(fieldId) })
        );
        return;
      }

      // Otherwise open modal with marker data
      this.showWeatherModal = false;
      this.ngZone.run(() => this.fieldDetailsDialog?.show(data));
    } catch {
      /* ignore */
    }
  }

  openWeatherModal(obj: Field | Region, lat: number, long: number, open = false): void {
    this.clickedObj = obj;
    this.clickedLatLong = [lat, long];
    this.showWeatherModal = open;
  }

  protected override export(): void {
    this.showModalExport = true;
  }
}
